import { useEffect, useState } from "react";
import ClientCard from "./clientCard";
import CUSTOMERS from "../../api/services/CUSTOMERS";
import styles from "../../styles/_containers/_clients/clientsList.module.scss";

const PAGE_SIZE = 20;

const ClientsList = ({ search, success, setSuccess, setIsOpenModal }) => {
  const [clientsList, setClientsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [page, setPage] = useState(1);
  const [type] = useState("all");
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setSuccess(null);
    const fetchData = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const res = await CUSTOMERS.get(page, PAGE_SIZE, type, debouncedSearch);
        if (res && res.code === 200) {
          const rows = Array.isArray(res.data) ? res.data : [];
          setClientsList(rows);
          setTotalPages(Math.max(1, res.totalPages || 1));
          setTotalItems(res.totalItems ?? res.total ?? null);
        } else {
          setClientsList([]);
          setTotalPages(1);
          setTotalItems(null);
        }
      } catch (error) {
        console.log(error);
        setClientsList([]);
        setTotalPages(1);
        setTotalItems(null);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [success, debouncedSearch, page, type, setSuccess]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section className={styles.container} aria-label="טבלת לקוחות">
      <div className={styles.listMeta}>
        <span>
          {loading
            ? "טוען לקוחות..."
            : debouncedSearch
              ? `${clientsList.length} תוצאות לחיפוש`
              : totalItems !== null
                ? `${totalItems} לקוחות`
                : `${clientsList.length} לקוחות בעמוד`}
        </span>
        <span className={styles.pageMeta}>עמוד {page} מתוך {totalPages}</span>
      </div>

      <div className={styles.table} role="table" aria-rowcount={clientsList.length}>
        <div className={styles.tableHeader} role="row">
          <span role="columnheader">לקוח</span>
          <span role="columnheader">טלפון</span>
          <span role="columnheader">פעולות מהירות</span>
          <span role="columnheader">כרטיס לקוח</span>
          <span role="columnheader" aria-label="פרטים נוספים" />
        </div>

        {loading ? (
          <div className={styles.skeletonList} aria-label="טוען לקוחות" role="status">
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <div className={styles.skeletonRow} key={item}>
                <span /><span /><span /><span />
              </div>
            ))}
          </div>
        ) : loadError ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon} aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none"><path d="M12 8v5M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /><path d="M10.3 4.5 3.5 17a2 2 0 0 0 1.76 3h13.48a2 2 0 0 0 1.76-3L13.7 4.5a1.95 1.95 0 0 0-3.4 0Z" stroke="currentColor" strokeWidth="1.7" /></svg>
            </span>
            <strong>לא הצלחנו לטעון את הלקוחות</strong>
            <p>כדאי לבדוק את החיבור ולנסות שוב</p>
            <button type="button" onClick={() => setSuccess((value) => !value)}>ניסיון נוסף</button>
          </div>
        ) : clientsList.length === 0 ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon} aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none"><circle cx="9" cy="8" r="4" stroke="currentColor" strokeWidth="1.7" /><path d="M2.5 20v-1a6.5 6.5 0 0 1 13 0v1M18 8v6M15 11h6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
            </span>
            <strong>{debouncedSearch ? "לא נמצאו לקוחות מתאימים" : "עדיין אין לקוחות ברשימה"}</strong>
            <p>{debouncedSearch ? "אפשר לנסות שם או מספר טלפון אחר" : "לקוחות חדשים יופיעו כאן"}</p>
          </div>
        ) : (
          <div className={styles.rows} role="rowgroup">
            {clientsList.map((client) => (
              <ClientCard
                key={client._id}
                id={client._id}
                userId={client.userId}
                businessId={client.businessId}
                phone={client.user?.phone || ""}
                name={client.user?.name || "לקוח ללא שם"}
                img={client.user?.profile}
                setIsOpenModal={setIsOpenModal}
              />
            ))}
          </div>
        )}
      </div>

      {!loading && !loadError && clientsList.length > 0 && totalPages > 1 && (
        <nav className={styles.pagination} aria-label="ניווט בין עמודי לקוחות">
          <button type="button" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m12.5 5-5 5 5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            הקודם
          </button>
          <span>{page} / {totalPages}</span>
          <button type="button" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>
            הבא
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m7.5 5 5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </nav>
      )}
    </section>
  );
};

export default ClientsList;
